// Fixed display prices per product per currency.
// These are used for display ONLY — Shopify CAD prices still drive cart operations.

// Single source of truth for free-shipping thresholds per currency.
export const FREE_SHIPPING_THRESHOLDS: Record<string, number> = {
  USD: 50,
  GBP: 35,
  AUD: 60,
  ILS: 180,
  EUR: 45,
  NOK: 500,
  AED: 180,
};

export const FIXED_PRICES: Record<string, Record<string, { sale: number; original: number }>> = {
  'pdrn-pink-sheet-masks-10-pack': {
    USD: { sale: 49, original: 69 },
    GBP: { sale: 34, original: 49 },
    AUD: { sale: 49, original: 75 },
    ILS: { sale: 119, original: 159 },
    CAD: { sale: 68, original: 96 },
    EUR: { sale: 45, original: 79 },
    NOK: { sale: 509, original: 899 },
    AED: { sale: 199, original: 349 },
  },
  'pink-glow-serum-with-pdrn-peptides': {
    USD: { sale: 39, original: 59 },
    GBP: { sale: 25, original: 39 },
    AUD: { sale: 39, original: 59 },
    ILS: { sale: 119, original: 179 },
    CAD: { sale: 54, original: 82 },
    EUR: { sale: 34, original: 69 },
    NOK: { sale: 389, original: 780 },
    AED: { sale: 149, original: 300 },
  },
  'smart-capsule-cream-with-collagen': {
    USD: { sale: 39, original: 59 },
    GBP: { sale: 29, original: 45 },
    AUD: { sale: 49, original: 75 },
    ILS: { sale: 129, original: 189 },
    CAD: { sale: 54, original: 82 },
    EUR: { sale: 34, original: 69 },
    NOK: { sale: 389, original: 780 },
    AED: { sale: 149, original: 300 },
  },
  'complete-pdrn-trio-bundle': {
    USD: { sale: 79, original: 169 },
    GBP: { sale: 59, original: 119 },
    AUD: { sale: 99, original: 179 },
    ILS: { sale: 229, original: 509 },
    CAD: { sale: 109, original: 234 },
    EUR: { sale: 79, original: 149 },
    NOK: { sale: 899, original: 1699 },
    AED: { sale: 349, original: 649 },
  },
};

/**
 * Get the fixed display price for a product in a given currency.
 * Returns null if no fixed price is defined (fallback to dynamic conversion).
 */
export function getFixedPrice(handle: string, currency: string): { sale: number; original: number } | null {
  return FIXED_PRICES[handle]?.[currency] || null;
}

/**
 * Format a fixed price amount for display (always whole numbers, no decimals).
 */
export function formatFixedPrice(amount: number, currency: string): string {
  const locale = currency === 'ILS' ? 'he-IL'
    : currency === 'GBP' ? 'en-GB'
    : currency === 'AUD' ? 'en-AU'
    : currency === 'CAD' ? 'en-CA'
    : currency === 'EUR' ? 'de-DE'
    : currency === 'NOK' ? 'nb-NO'
    : currency === 'AED' ? 'ar-AE'
    : 'en-US';

  let formatted = new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);

  // Ensure AUD shows "AU$" for clarity
  if (currency === 'AUD') {
    if (formatted.includes('AU$')) {
      // already correct
    } else if (formatted.includes('A$')) {
      formatted = formatted.replace('A$', 'AU$');
    } else {
      formatted = formatted.replace('$', 'AU$');
    }
  }

  // Ensure CAD shows "C$" for clarity
  if (currency === 'CAD') {
    if (formatted.includes('C$') || formatted.includes('CA$')) {
      // already has prefix
    } else {
      formatted = formatted.replace('$', 'C$');
    }
  }

  return formatted;
}

/**
 * Calculate the total display price for a list of cart items in the given currency.
 */
export function getFixedPriceTotal(
  items: Array<{ handle: string; quantity: number }>,
  currency: string
): number {
  return items.reduce((sum, item) => {
    const fp = getFixedPrice(item.handle, currency);
    return sum + (fp ? fp.sale * item.quantity : 0);
  }, 0);
}
